import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { subscriptionService } from '../services/api';
import { useAuth } from './AuthContext';

export type SubscriptionStatus = 'active' | 'grace_period' | 'expired' | 'pending' | 'none';

export interface SubscriptionData {
  id: string;
  plan_name: 'trial' | 'monthly' | 'quarterly' | 'yearly';
  status: string;
  computed_status: SubscriptionStatus;
  farm_count: number;
  total_amount: number;
  base_price: number;
  farm_surcharge: number;
  starts_at: string;
  ends_at: string;
  grace_ends_at: string;
  days_remaining: number;
  grace_days_remaining: number;
  is_trial?: boolean;
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  isLoading: boolean;
  isActive: boolean;
  isGrace: boolean;
  isExpired: boolean;
  daysRemaining: number;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ── Use refs to avoid stale closures without adding to deps ──
  const userIdRef = useRef(user?.id);
  const isAuthRef = useRef(isAuthenticated);
  const userRoleRef = useRef(user?.role);

  useEffect(() => {
    userIdRef.current = user?.id;
    isAuthRef.current = isAuthenticated;
    userRoleRef.current = user?.role;
  });

  const refreshSubscription = useCallback(async () => {
    if (!isAuthRef.current || !userIdRef.current || userRoleRef.current !== 'super_admin') {
      setIsLoading(false);
      return;
    }
    try {
      // ── Do NOT set isLoading(true) on background refreshes ──
      // Only show loading on initial fetch (when subscription is null)
      const { data } = await subscriptionService.getMine();
      setSubscription(data.data || null);
    } catch {
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, []); // ── Empty deps: uses refs instead, so never recreated ──

  // ── Initial fetch when auth state changes ────────────────────
  useEffect(() => {
    // AuthContext itself is still resolving (e.g. right after a hard
    // refresh, before GET /auth/me returns) — isAuthenticated is only
    // `false` here because nothing has loaded yet, not because the user
    // really is logged out. Bailing out and setting isLoading(false) in
    // that transient window let ProtectedRoute's redirect check run with
    // subscription still null (isActive/isGrace both false), incorrectly
    // sending an actively-subscribed super_admin to /subscription on every
    // hard refresh. Wait for auth to settle before deciding anything.
    if (authLoading) return;

    if (!isAuthenticated || !user) {
      setIsLoading(false);
      setSubscription(null);
      return;
    }
    if (user.role !== 'super_admin') {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    refreshSubscription();
  }, [isAuthenticated, user?.id, authLoading]); // ── Only re-run when user ID/auth-loading changes, not full user object ──

  // ── Background poll — only when subscription is pending/expiring ──
  // Does NOT trigger loading state so no re-renders during typing
  useEffect(() => {
    if (!isAuthenticated || !user || user.role !== 'super_admin') return;

    const interval = setInterval(() => {
      // Only poll silently — no loading state change
      subscriptionService.getMine()
        .then(({ data }) => {
          setSubscription(prev => {
            const next = data.data || null;
            // Only update if something actually changed
            if (prev?.computed_status === next?.computed_status &&
                prev?.id === next?.id) return prev;
            return next;
          });
        })
        .catch(() => {});
    }, 60000); // ── 60s instead of 30s, and silent ──

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.id]); // ── user?.id not full user object ──

  const computedStatus = subscription?.computed_status || 'none';

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      isLoading,
      isActive: computedStatus === 'active',
      isGrace: computedStatus === 'grace_period',
      isExpired: computedStatus === 'expired' || computedStatus === 'none',
      daysRemaining: subscription?.days_remaining || 0,
      refreshSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};